import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { hu } from "../translations/hu";
import { en } from "../translations/en";
import {
  Language,
  DEFAULT_LANGUAGE as DEFAULT_LANG,
  LANGUAGE_STORAGE_KEY,
  isLanguageSupported,
} from "../config/languages";

type Translations = typeof hu;

interface TranslationContextType {
  t: Translations;
  language: Language;
  setLanguage: (lang: Language) => void;
  isLoading: boolean;
}

const isBrowser = () => typeof window !== "undefined";

const getStoredLanguage = (): Language | null => {
  if (!isBrowser()) return null;
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && isLanguageSupported(stored)) return stored as Language;
  } catch {}
  return null;
};

const getLanguageFromUrl = (): Language | null => {
  if (!isBrowser()) return null;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");
    if (langParam && isLanguageSupported(langParam)) return langParam as Language;
  } catch {}
  return null;
};

const storeLanguage = (language: Language): void => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {}
};

const updateUrlWithLanguage = (language: Language): void => {
  if (!isBrowser()) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("lang", language);
    window.history.replaceState({}, "", url.toString());
  } catch {}
};

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined
);

const translations = { hu, en: en as unknown as Translations } as Record<string, Translations>;

export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANG);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const urlLang = getLanguageFromUrl();
    const storedLang = getStoredLanguage();
    const initialLang = urlLang || storedLang || DEFAULT_LANG;

    setLanguageState(initialLang);
    setIsLoading(false);

    if ((urlLang || !storedLang) && isBrowser()) {
      storeLanguage(initialLang);
    }
    if (urlLang && isBrowser()) {
      updateUrlWithLanguage(urlLang);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    if (isLanguageSupported(lang)) {
      setLanguageState(lang);
      storeLanguage(lang);
      updateUrlWithLanguage(lang);
    }
  };

  const value = {
    t: translations[language],
    language,
    setLanguage,
    isLoading,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error(
      "useTranslations must be used within a TranslationProvider"
    );
  }
  return context;
};
